import type { FC } from "react";
import { useEffect, useState } from "react";

import type { IUserCommunicationTemplate } from "@/src/common/UserCommunicationTemplate";
import type { IUserCommunicationTemplateModal } from "@/src/common/UserCommunicationTemplateModal";
import UserCommunicationTemplateModal from "@/src/common/UserCommunicationTemplateModal";
import useAjax, { AjaxMethod } from "@Components/Hooks/useAjax";
import MainLoading from "@Components/MainLoading";
import { urlFormat } from "@Components/Modules/Helpers";
import { BankIntegration } from "@Routes/BankIntegrationLinks";
import { Error } from "@Routes/ErrorHandlingLinks";
import * as stylex from "@stylexjs/stylex";

import * as css from "./RedirectPage.module.css";

export type IRedirect = {
    redirectUrl: string;
    token: string;
};

const styles = stylex.create({
    wrapper: {
        display: "grid",
        height: "100vh",
    },
});

const RedirectPage: FC<IRedirect> = ({ redirectUrl, token }) => {
    const [loading, setLoading] = useState(true);
    const redirectAction = () => window.open(redirectUrl, "_self", "noopener");

    const { data, errorContainer, request } = useAjax<IUserCommunicationTemplate>({
        ajaxAction: urlFormat(BankIntegration, { action: "UserAuthenticate" }),
        ajaxMethod: AjaxMethod.Post,
        success: (response) => {
            if (response) {
                setLoading(false);
            } else {
                redirectAction();
            }
        },
        fail: (_, xhr) => {
            if (xhr.status === 400) {
                window.open(Error, "_self", "noopener");
                return;
            }
            setLoading(false);
        },
        onErrorContainerClose: () => {
            setLoading(true);
            window.open("/", "_self", "noopener");
        },
    });

    useEffect(() => {
        request(null, { token });
    }, []);

    const commModalUpdated: IUserCommunicationTemplateModal = {
        commBase: {
            ...data,
            onNext: redirectAction,
        },
        modal: {
            className: css.customUserComm,
            closeOnEsc: false,
            closeOnBlur: false,
            onClose: redirectAction,
            closeIconClickTelemetry: {
                eventName: "common-exit",
                actionType: "Click",
            },
            trackingUrlCustom: {
                eventName: "cshbck-BankOutbound",
                actionType: "View",
            },
        },
    };

    return (
        <div {...stylex.props(styles.wrapper)}>
            {loading && <MainLoading />}
            {data && <UserCommunicationTemplateModal {...commModalUpdated} />}
            {errorContainer}
        </div>
    );
};

export { RedirectPage };
